<template>
  <div class="main-container">
    <h1>{{ $t("entreprises.title") }}</h1>
   <div
  class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between"
>
  <div class="d-flex">
    <button
      type="button"
      class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
      @click="add"
    >
      <b-icon
        icon="plus"
        class="mr-1"
        scale="1.5"
        aria-hidden="true"
        variant="danger"
      />
      {{ $t('entreprises.add') }}
    </button>
  </div>
  <div class="d-flex">
    <button
      type="button"
      class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
      :disabled="!hasModifications"
      @click="save"
    >
      {{ $t('entreprises.save') }}
    </button>
  </div>
</div>
    <DataTable
      ref="table"
      :columns="dtColumns"
      :url="url"
      trackBy="entreprise.id"
      :searchable="true"
      :searchLabel="$t('translations.search').toString()"
      :parseResponse="parseResponse"
      :rowClass="rowClassFn"
    >
      <!-- Name -->
      <template #editable-name="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.translation.name"
          @input="markModified(row)"
        />
      </template>
      <!-- idSap -->
      <template #editable-idSap="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.entreprise.idSap"
          @input="markModified(row)"
        />
      </template>
      <!-- Sites -->
      <template #sitesCell="{ row }">
        <EntrepriseSitesCell
          :row="row"
          @edit-sites="openSites"
        />
      </template>
      <!-- BOPs / categories -->
      <template #bopsCell="{ row }">
        <EntrepriseCategoryCell
          :row="row"
          @edit-category="openBops"
        />
      </template>
      <!-- Enabled switch -->
      <template #switch-isEnabled="{ row }">
        <button
          type="button"
          class="btn btn-switch toggle-switch"
          :class="row.entreprise.isEnabled ? 'active-last' : 'active-first'"
          @click="toggleIsEnabled(row)"
        >
          <span class="switch-toggler" />
        </button>
      </template>
    </DataTable>
    <div
  class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between"
>
  <div class="d-flex">
    <button
      type="button"
      class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
      @click="add"
    >
      <b-icon
        icon="plus"
        class="mr-1"
        scale="1.5"
        aria-hidden="true"
        variant="danger"
      />
      {{ $t('entreprises.add') }}
    </button>
  </div>
  <div class="d-flex">
    <button
      type="button"
      class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
      :disabled="!hasModifications"
      @click="save"
    >
      {{ $t('entreprises.save') }}
    </button>
  </div>
</div>
    <SitesTableModal
      :show="showSitesModal"
      @close="showSitesModal = false"
      @validate="validate"
      :props="propsSitesModal"
    />
    <BopTableModal
      :show="showBopModal"
      @close="showBopModal = false"
      @validate="validate"
      :props="propsBopModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import Entreprise from "@/models/Entreprise";
import EntreprisesServices from "@/services/EntreprisesServices";
import { Guid } from "guid-typescript";
import Constants from "@/utils/Constants";

type Data = {
  columns: Array<Object>;
  rows: Array<Object>;
};

@Component({
  components: {
    DataTable: () => import("@/components/Shared/Table/DataTable.vue"),
    EntrepriseSitesCell: () =>
      import("@/components/Entreprises/EntrepriseSitesCell.vue"),
    EntrepriseCategoryCell: () =>
      import("@/components/Entreprises/EntrepriseCategoryCell.vue"),
    SitesTableModal: () =>
      import("@/components/Shared/Table/SitesTableModal.vue"),
    BopTableModal: () => import("@/components/Shared/Table/BopTableModal.vue"),
  },
})
export default class Entreprises extends Vue {
  // Keep track of modified Objects
  private modifiedEntreprises: Array<Object> = [];
  // API's Url
  private url: string = EntreprisesServices.getTableUrlByAdministrator(
    this.$i18n.locale
  );

  // Props for Modal on categoy field
  // Sites Modal
  private showSitesModal: Boolean = false;
  private propsSitesModal: any = false;
  // Bops Modal
  private showBopModal: Boolean = false;
  private propsBopModal: any = false;

  public data: Data = {
    columns: [],
    rows: [],
  };
  private selectedRow: string[] = [];

  private get dtColumns(): Array<{ key: string; label: string; slot?: string; sortField?: string; sortable?: boolean }> {
    return [
      {
        key: "name",
        label: this.$t("entreprises.name").toString(),
        sortField: "translation.name",
        sortable: true,
        slot: "editable-name",
      },
      {
        key: "idSap",
        label: this.$t("entreprises.idsap").toString(),
        sortField: "entreprise.idSap",
        sortable: true,
        slot: "editable-idSap",
      },
      {
        key: "sites",
        label: this.$t("entreprises.sitesTitle").toString(),
        sortField: "entreprise.sites",
        sortable: false,
        slot: "sitesCell",
      },
      {
        key: "bops",
        label: this.$t("entreprises.bopsTitle").toString(),
        sortField: "entreprise.bopModels",
        sortable: false,
        slot: "bopsCell",
      },
      {
        key: "isEnabled",
        label: this.$t("entreprises.actifs").toString(),
        sortField: "entreprise.isEnabled",
        sortable: true,
        slot: "switch-isEnabled",
      },
    ];
  }

  private parseResponse(resp: any) {
    const data = resp?.data || [];
    this.data.rows = data;
    return {
      rows: data,
      pagination: resp?.pagination,
    };
  }

  // Validate button on modals
  public validate(entreprise: Entreprise) {
    this.markModified(entreprise);
    this.showSitesModal = false;
    this.showBopModal = false;
  }

  // Open modal based on property
  public openModal(props: any) {
    if (props.rowField.property == "entreprise.sites") {
      this.showSitesModal = true;
      this.propsSitesModal = props;
    } else if (props.rowField.property == "entreprise.bopModels") {
      this.showBopModal = true;
      this.propsBopModal = props;
    }
  }

  // Refresh Table
  public refresh(): void {
    this.modifiedEntreprises = [];

  }

  // Add new Object in the table and push into modified objects
  public add(): void {
    const newEntreprise = {
      entreprise: new Entreprise(),
      translation: {
        id: Guid.create().toString(),
        language: {
          code: "fr",
        },
        name: "",
      },
    };
    const api: any = (this.$refs.table as any)?.api;
    if (api && api.rows) {
      api.rows.unshift(newEntreprise);
    }
    this.markModified(newEntreprise as any);
    const id = (newEntreprise as any).entreprise?.id || (newEntreprise as any).translation?.id;
    if (id && !this.selectedRow.includes(id)) this.selectedRow.push(id);
  }

  // Save Modifications based on modified objects
  public async save() {
    let canRefresh = true;
    for (let i = 0; i < this.modifiedEntreprises.length; i++) {
      try {
        const modifiedEntreprise = this.modifiedEntreprises[i];
        if ((<any>modifiedEntreprise).translation.name == "") {
          this.sendErrorNotification(
            this.$t("entreprise.updateFailed").toString(),
            this.$t("entreprise.propertyNameEmpty").toString()
          );
          canRefresh = false;
          break;
        } else if ((<any>modifiedEntreprise).entreprise.idSap == "") {
          this.sendErrorNotification(
            this.$t("entreprise.updateFailed").toString(),
            this.$t("entreprise.propertyIdSapEmpty").toString()
          );
          canRefresh = false;
          break;
        }
        await EntreprisesServices.addEntreprise(
          modifiedEntreprise,
          this.$i18n.locale
        );
               this.$notify({
        group: "global",
        type: "success",
        title: this.$t('generic.updateSuccess.title').toString(),
        text: this.$t('entreprises.updateSuccess.message').toString(),
      });        
       // Reload table rows from server to reflect persisted changes
          const table:any = this.$refs.table;
          if (table && table.api && this.url) {
            await table.api.load(this.url);
          }
          // Reset local modification tracking and selection
          this.modifiedEntreprises = [];
      } catch (err: any) {
        let errorText: string = this.$t("entreprise.uknwownError").toString();
        if (err && err.response && err.response.status === 400) {
          const code = err.response && err.response.data && err.response.data.errorCode;
          if (code == "idSapNotValid") {
            errorText = this.$t("entreprise.idSapNotValid").toString();
          } else if (code === "duplicateIdSap") {
            errorText = this.$t("entreprise.duplicateIdSap").toString();
          } else {
            errorText = this.$t("entreprise.incorrectValues").toString();
          }
        }
        this.sendErrorNotification(
          this.$t("entreprise.updateFailed").toString(),
          errorText
        );

        canRefresh = false;
      }
    }
    if (canRefresh) {
      this.refresh();
    }
  }

  private sendErrorNotification(title: string, text: string) {
    this.$notify({
      group: "globalError",
      type: "error",
      title,
      text,
      duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
    });
  }
  @Watch("$i18n.locale")
  private onLocaleChange() {
    this.url = EntreprisesServices.getTableUrlByAdministrator(
      this.$i18n.locale
    );
    this.$forceUpdate();
  }

  get hasModifications(): boolean {
    return this.modifiedEntreprises.length > 0;
  }

  private markModified(row: any) {
    const id = row.entreprise?.id || row.entreprise?.idSap || row.translation?.id;
    const idx = this.modifiedEntreprises.findIndex((x: any) => {
      const xId = x.entreprise?.id || x.entreprise?.idSap || x.translation?.id;
      return xId === id;
    });
    if (idx === -1) {
      this.modifiedEntreprises.push(row);
    } else {
      this.modifiedEntreprises.splice(idx, 1, row);
    }
  }

  public openSites(props: any) {
    this.showSitesModal = true;
    this.propsSitesModal = props;
  }

  public openBops(props: any) {
    this.showBopModal = true;
    this.propsBopModal = props;
  }

  private toggleIsEnabled(row: any) {
    row.entreprise.isEnabled = !row.entreprise.isEnabled;
    this.markModified(row);
  }

  private rowClassFn(row:any) {
    const id = row.entreprise?.id || row.translation?.id;
    return this.selectedRow.includes(id) ? 'selected-row' : '';
  }
}
</script>
